import { useCallback, useEffect, useMemo, useState } from 'react';
import type { WorkingIntervalDto } from '../../api/cabinetTypes';
import { useAppApi } from '../../app/ApiProvider';
import { useAsync } from '../../hooks/useAsync';
import type { UiKey } from '../../i18n/dict';
import { useI18n } from '../../i18n/I18nProvider';
import { BottomSheet } from '../../ui/BottomSheet';
import { Chip } from '../../ui/controls';
import { CtaBar, CtaButton, SmallButton } from '../../ui/Cta';
import { Alert, ErrorState, Loader } from '../../ui/feedback';
import { Screen, ScrollArea, Section, SectionTitle, TopBar } from '../../ui/Screen';
import { Field, FieldPair, FormError, Note, Row, TextInput, Toggle } from './settings/parts';
import {
  buildWeek,
  dayProblem,
  flattenWeek,
  formatDaySummary,
  formatHhMm,
  parseHhMm,
  sameSchedule,
  weekProblem,
  withDay,
  type DraftInterval,
  type ScheduleProblemKey,
  type WeekDraft,
} from './settings/week';
import { useBusiness } from './useBusiness';

/** Ключи дней. Индекс = `weekday`: 0 = воскресенье, как в JS `Date.getDay()`. */
const DOW_KEYS: readonly UiKey[] = ['dow0', 'dow1', 'dow2', 'dow3', 'dow4', 'dow5', 'dow6'];

/**
 * Порядок показа — с понедельника.
 *
 * Хранение начинается с воскресенья (так устроен `Date.getDay()` и колонка `weekday`),
 * но неделя в Узбекистане и России начинается в понедельник, и список, открывающийся
 * воскресеньем, читается как ошибка. Порядок показа и порядок хранения — разные вещи.
 */
const DISPLAY_ORDER: readonly number[] = [1, 2, 3, 4, 5, 6, 0];

/**
 * «Часы работы» кабинета (ТЗ §14.1). Два уровня: часы организации и личные часы мастера.
 *
 * ЛИЧНЫЕ ЧАСЫ ПЕРЕКРЫВАЮТ ЧАСЫ ОРГАНИЗАЦИИ, А НЕ СКЛАДЫВАЮТСЯ С НИМИ, и пустой личный
 * набор означает «работает по часам организации», а не «не работает». Это сказано текстом
 * (`schStaffNote`): без него владелец, увидев пустую неделю у мастера, решил бы, что мастер
 * не работает вовсе, и завёл бы ему копию часов салона — которая перестала бы обновляться
 * вместе с ними.
 *
 * ЭКРАН РЕДАКТИРУЕТ ЛОКАЛЬНУЮ КОПИЮ НЕДЕЛИ И ОТПРАВЛЯЕТ ЕЁ ОДНИМ СОХРАНЕНИЕМ.
 * Это не выбор в пользу удобства: `replaceSchedule` — PUT, уровень заменяется целиком,
 * потому что пересечение интервалов — свойство пары, и проверить его можно только имея
 * набор целиком. При построчном сохранении вторая строка легла бы поверх первой, и обе
 * прошли бы проверку.
 */
export function ScheduleScreen(props: { businessId: string; onBack: () => void }): JSX.Element {
  const { t } = useI18n();
  const api = useAppApi();
  const { businessId } = props;

  const businessLoad = useBusiness(businessId);
  const staffList = useAsync(
    useCallback(() => api.cabinet.listStaff(businessId), [api, businessId]),
    [businessId],
  );

  /** `null` — часы всей организации. Иначе личные часы выбранного мастера. */
  const [staffId, setStaffId] = useState<string | null>(null);
  const [togglingOwn, setTogglingOwn] = useState(false);
  const [ownError, setOwnError] = useState<unknown>(null);

  const schedule = useAsync(
    useCallback(
      () => api.cabinet.getSchedule(businessId, staffId ?? undefined),
      [api, businessId, staffId],
    ),
    [businessId, staffId],
  );

  const [draft, setDraft] = useState<WeekDraft | null>(null);
  const [editing, setEditing] = useState<DayEdit | null>(null);
  const [dayProblemKey, setDayProblemKey] = useState<ScheduleProblemKey | null>(null);
  const [saveError, setSaveError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const scheduleState = schedule.state;

  // Черновик пересобирается при каждой загрузке уровня. Не сбросив его при смене мастера,
  // экран показал бы часы предыдущего и сохранил бы их не тому.
  useEffect(() => {
    setDraft(scheduleState.status === 'ready' ? buildWeek(scheduleState.data.intervals) : null);
    setEditing(null);
    setSaveError(null);
    setSaved(false);
  }, [scheduleState]);

  const loaded: readonly WorkingIntervalDto[] =
    scheduleState.status === 'ready' ? scheduleState.data.intervals : [];

  const problem = useMemo(() => (draft ? weekProblem(draft) : null), [draft]);
  const dirty = useMemo(
    () => (draft ? !sameSchedule(flattenWeek(draft), loaded) : false),
    [draft, loaded],
  );

  const save = async (): Promise<void> => {
    if (!draft || saving || problem) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Клиентская проверка уже прошла, но полагаться на неё нельзя: пересечения и кривые
      // интервалы отвергает бэкенд (`err.workingHoursOverlap`, `err.workingHoursInvalid`),
      // и его отказ показывается тем же текстом.
      await api.cabinet.replaceSchedule(businessId, flattenWeek(draft), staffId ?? undefined);
      setSaved(true);
      schedule.reload();
    } catch (error) {
      setSaveError(error);
    } finally {
      setSaving(false);
    }
  };

  const applyDay = (): void => {
    if (!editing || !draft) return;

    const intervals: DraftInterval[] = [];
    for (const row of editing.rows) {
      const startMin = parseHhMm(row.from);
      const endMin = parseHhMm(row.to);
      if (startMin === null || endMin === null) {
        setDayProblemKey('err.workingHoursInvalid');
        return;
      }
      intervals.push({ startMin, endMin });
    }

    const found = dayProblem(intervals, editing.weekday);
    if (found) {
      setDayProblemKey(found.key);
      return;
    }

    setDraft(withDay(draft, editing.weekday, intervals));
    setEditing(null);
    setDayProblemKey(null);
    setSaved(false);
  };

  const business = businessLoad.business;

  if (businessLoad.state.status === 'failed') {
    return (
      <Screen>
        <TopBar title={t('schTitle')} onBack={props.onBack} />
        <ErrorState error={businessLoad.state.error} onRetry={businessLoad.reload} />
      </Screen>
    );
  }

  if (staffList.state.status === 'failed') {
    return (
      <Screen>
        <TopBar title={t('schTitle')} onBack={props.onBack} />
        <ErrorState error={staffList.state.error} onRetry={staffList.reload} />
      </Screen>
    );
  }

  const staff = staffList.state.status === 'ready' ? staffList.state.data : [];
  const selected = staffId === null ? null : (staff.find((m) => m.id === staffId) ?? null);

  /**
   * Переключить источник расписания у мастера.
   *
   * После успеха перечитывается СПИСОК специалистов, а не только флаг в локальном состоянии:
   * положение тумблера — это серверное значение, и хранить его отдельной копией значит
   * рано или поздно показать включённым то, что на сервере выключено.
   */
  const toggleOwnSchedule = async (on: boolean): Promise<void> => {
    if (staffId === null || togglingOwn) return;
    setTogglingOwn(true);
    setOwnError(null);
    try {
      await api.cabinet.setStaffOwnSchedule(businessId, staffId, on);
      staffList.reload();
    } catch (failure) {
      setOwnError(failure);
    } finally {
      setTogglingOwn(false);
    }
  };

  return (
    <Screen>
      <TopBar
        title={t('schTitle')}
        subtitle={business?.name ?? undefined}
        onBack={props.onBack}
      />

      <ScrollArea bottomGap="cta">
        <Section>
          <SectionTitle>{t('schLevelTitle')}</SectionTitle>
          <div className="no-scrollbar flex gap-2 overflow-x-auto py-0.5">
            <Chip active={staffId === null} onClick={() => setStaffId(null)}>
              {t('schBusinessLevel')}
            </Chip>
            {staff.map((member) => (
              <Chip
                key={member.id}
                active={member.id === staffId}
                onClick={() => setStaffId(member.id)}
              >
                {member.name}
              </Chip>
            ))}
          </div>
          {selected ? (
            <>
              {/* Главный переключатель экрана. Пока он выключен, личные часы не действуют
                  вовсе; включённый — делает день без интервалов выходным. */}
              <div className="mt-2.5">
                <Toggle
                  on={selected.usesOwnSchedule}
                  disabled={togglingOwn}
                  label={t('schOwnToggle')}
                  onChange={(on) => void toggleOwnSchedule(on)}
                />
              </div>
              <Note>{selected.usesOwnSchedule ? t('schOwnOnNote') : t('schOwnOffNote')}</Note>
              <Note>{t('schOwnKeepNote')}</Note>
              {ownError ? <ErrorState error={ownError} /> : null}
            </>
          ) : null}
        </Section>

        <Section>
          <SectionTitle>{t('schWeek')}</SectionTitle>

          {scheduleState.status === 'loading' || !draft ? (
            <Loader />
          ) : scheduleState.status === 'failed' ? (
            <ErrorState error={scheduleState.error} onRetry={schedule.reload} />
          ) : (
            <>
              {DISPLAY_ORDER.map((weekday) => {
                const day = draft[weekday] ?? [];
                const summary = formatDaySummary(day);
                return (
                  <Row
                    key={weekday}
                    onClick={() => {
                      setDayProblemKey(null);
                      setEditing({ weekday, rows: day.map(toRow) });
                    }}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className="text-[14.5px] font-semibold">
                        {t(DOW_KEYS[weekday] ?? 'dow0')}
                      </span>
                      <span
                        className={`text-[13px] font-semibold ${
                          summary ? 'text-ink-2' : 'text-ink-3'
                        }`}
                      >
                        {summary || t('schClosed')}
                      </span>
                    </div>
                  </Row>
                );
              })}

              {/* Пустая неделя организации — законное состояние (закрыта), но записаться
                  к ней нельзя вовсе, и владелец обязан понимать, почему нет слотов. */}
              {staffId === null && flattenWeek(draft).length === 0 ? (
                <Alert tone="warn">{t('schEmpty')}</Alert>
              ) : null}

              {business ? <Note>{t('schTzNote', { tz: business.tz })}</Note> : null}
              {problem ? (
                <FormError
                  text={`${t(DOW_KEYS[problem.weekday] ?? 'dow0')} · ${t(problem.key)}`}
                />
              ) : dirty ? (
                <Note>{t('schDirty')}</Note>
              ) : saved ? (
                <Note>{t('schSaved')}</Note>
              ) : null}

              {saveError ? <ErrorState error={saveError} /> : null}
            </>
          )}
        </Section>
      </ScrollArea>

      <CtaBar>
        <CtaButton
          onClick={() => void save()}
          disabled={!draft || saving || !dirty || problem !== null}
        >
          {t('schSave')}
        </CtaButton>
      </CtaBar>

      <BottomSheet
        open={editing !== null}
        title={
          editing ? `${t('schEditDay')} · ${t(DOW_KEYS[editing.weekday] ?? 'dow0')}` : undefined
        }
        onClose={() => {
          setEditing(null);
          setDayProblemKey(null);
        }}
      >
        {editing ? (
          <>
            {/* Пустой список интервалов — это выходной. Отдельного флага «выходной» нет:
                два способа сказать одно и то же расходятся на первой же правке. */}
            {editing.rows.length === 0 ? <Note>{t('schClosed')}</Note> : null}

            {editing.rows.map((row, index) => (
              <div key={index} className="mb-1">
                <FieldPair>
                  <Field label={t('schFrom')}>
                    <TextInput
                      value={row.from}
                      onChange={(value) => setEditing(withRow(editing, index, { ...row, from: value }))}
                      placeholder="10:00"
                    />
                  </Field>
                  <Field label={t('schTo')}>
                    <TextInput
                      value={row.to}
                      onChange={(value) => setEditing(withRow(editing, index, { ...row, to: value }))}
                      placeholder="21:00"
                    />
                  </Field>
                </FieldPair>
                <div className="mb-3 flex">
                  <SmallButton
                    variant="warn"
                    onClick={() =>
                      setEditing({
                        weekday: editing.weekday,
                        rows: editing.rows.filter((_, position) => position !== index),
                      })
                    }
                  >
                    {t('schRemoveInterval')}
                  </SmallButton>
                </div>
              </div>
            ))}

            {dayProblemKey ? <FormError text={t(dayProblemKey)} /> : null}

            <CtaButton
              variant="ghost"
              onClick={() =>
                setEditing({
                  weekday: editing.weekday,
                  // Пустые поля, а не «10:00–21:00» по умолчанию: подставленное время
                  // владелец сохранил бы не глядя, и салон открылся бы в час, который
                  // выбрал не он.
                  rows: [...editing.rows, { from: '', to: '' }],
                })
              }
              className="mb-2"
            >
              {t('schAddInterval')}
            </CtaButton>
            <CtaButton onClick={applyDay}>{t('schDayDone')}</CtaButton>
          </>
        ) : null}
      </BottomSheet>
    </Screen>
  );
}

/** Правка одного дня живёт СТРОКАМИ, а не минутами: «1» в поле ещё не время. */
interface DayEdit {
  readonly weekday: number;
  readonly rows: readonly TimeRow[];
}

interface TimeRow {
  readonly from: string;
  readonly to: string;
}

function toRow(interval: DraftInterval): TimeRow {
  return { from: formatHhMm(interval.startMin), to: formatHhMm(interval.endMin) };
}

function withRow(edit: DayEdit, index: number, row: TimeRow): DayEdit {
  return {
    weekday: edit.weekday,
    rows: edit.rows.map((current, position) => (position === index ? row : current)),
  };
}
